// src/components/ui/BrandedSpinner.tsx

/**
 * @fileoverview BrandedSpinner — a premium, on-brand loading spinner.
 *
 * Two concentric gradient arcs counter-rotate at different speeds over a
 * softly pulsing core, replacing the generic single-border `animate-spin`
 * spinners. Pure CSS transforms (GPU-compositable) drive the motion, and the
 * whole thing is gated behind Tailwind's `motion-safe`/`motion-reduce`
 * variants so it freezes to a clean static ring when the user prefers reduced
 * motion. Decorative by default (`aria-hidden`); pass a `label` to announce a
 * loading state to assistive tech.
 *
 * Uses `useId` for a per-instance gradient id so multiple spinners on one page
 * never collide on a duplicate DOM id.
 */

'use client'

import { useId } from 'react'
import { cn } from '~/lib/utils'

const SIZES = {
  sm: 24,
  md: 40,
  lg: 64,
} as const

export interface BrandedSpinnerProps {
  /** Diameter preset. Defaults to `md` (40px). */
  size?: keyof typeof SIZES
  /**
   * Accessible label. When provided the spinner is announced as a live status
   * region; when omitted the spinner is purely decorative (`aria-hidden`).
   */
  label?: string
  className?: string
}

export function BrandedSpinner({ size = 'md', label, className }: BrandedSpinnerProps): JSX.Element {
  const px = SIZES[size]
  // Per-instance gradient id (colons stripped — invalid in url(#…) references).
  const gradId = `bs-${useId().replace(/:/g, '')}`
  return (
    <span
      className={cn('relative inline-flex items-center justify-center', className)}
      style={{ width: px, height: px }}
      {...(label
        ? { role: 'status', 'aria-live': 'polite', 'aria-label': label }
        : { 'aria-hidden': true })}
    >
      <svg
        viewBox="0 0 48 48"
        width={px}
        height={px}
        fill="none"
        className="overflow-visible"
        aria-hidden="true"
      >
        <defs>
          <linearGradient id={gradId} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#6366F1" />
            <stop offset="100%" stopColor="#22D3EE" />
          </linearGradient>
        </defs>

        {/* Track */}
        <circle cx="24" cy="24" r="20" className="stroke-gray-200 dark:stroke-gray-800" strokeWidth="4" />

        {/* Outer arc — clockwise */}
        <circle
          cx="24"
          cy="24"
          r="20"
          stroke={`url(#${gradId})`}
          strokeWidth="4"
          strokeLinecap="round"
          strokeDasharray="48 200"
          className="origin-center motion-safe:animate-spin [animation-duration:1.1s] motion-reduce:animate-none"
        />

        {/* Inner arc — counter-clockwise, faster */}
        <circle
          cx="24"
          cy="24"
          r="11"
          stroke={`url(#${gradId})`}
          strokeWidth="3"
          strokeLinecap="round"
          strokeDasharray="20 120"
          opacity="0.7"
          className="origin-center motion-safe:animate-spin [animation-duration:0.8s] [animation-direction:reverse] motion-reduce:animate-none"
        />

        {/* Pulsing core */}
        <circle
          cx="24"
          cy="24"
          r="3.5"
          fill={`url(#${gradId})`}
          className="origin-center motion-safe:animate-pulse motion-reduce:animate-none"
        />
      </svg>
      {label ? <span className="sr-only">{label}</span> : null}
    </span>
  )
}

export default BrandedSpinner
